import React, { Fragment } from 'react';
import ComponentName from '../constant/ComponentName';
import { FormicaProps, withFormica } from '../helpers/withFormica';
import { RootState } from '../redux/store';
import { changePageFilter, clearPage } from '../redux/actions/PageActions';
import { connect } from 'react-redux';
import { ConnectedProps } from 'react-redux/es/exports';
import ListViewerListComponent from '../components/list/ListViewerListComponent';
import CollectionNames from '../constant/CollectionNames';
import { OverlayPanel } from 'primereact/overlaypanel';
import { List, ListSchema } from '../domain/list/ListDomains';
import ListService from '../service/ListService';
import ServiceContainer from '../config/ServiceContainer';
import { ServiceNames } from '../constant/ServiceNames';
import RoleHelper from '../RoleHelper';
import { ComponentState, setSelectedItems } from '../redux/actions/ComponentActions';
import CustomDismissible from '../components/custom/CustomDismissible';
import StandardContainerComponent, { StandardSubscriptionComponentArgs } from '../helpers/StandardContainerComponent';
import LanguageUtil from '../util/LanguageUtil';
import { BulkActionDependencyError } from 'redux/reducers/BulkActionReducer';

class ListContainer extends StandardContainerComponent<Props, State, List, ListSchema> {
  private readonly listService: ListService = ServiceContainer.getInstance().get(ServiceNames.LIST);

  private createOverlayRef: OverlayPanel | null = null;

  constructor(props: Props) {
    const args: StandardSubscriptionComponentArgs = {
      component: ComponentName.LIST,
      withSchema: true,
      itemCollection: CollectionNames.LISTS,
      label: LanguageUtil.get({ id: 'common.label.lists', defaultMessage: 'Lists' }),
    };
    super(props, args);
  }

  public render() {
    return (
      <Fragment>
        {super.render()}
        <OverlayPanel ref={(el) => (this.createOverlayRef = el)} dismissable onHide={() => this.createOverlayRef?.hide()}>
          {this.getCreateItems()}
        </OverlayPanel>
      </Fragment>
    );
  }

  protected getListView(): React.ReactNode {
    const { items, itemBuilderView, permissions } = this.state;
    return (
      <ListViewerListComponent
        lists={items}
        views={itemBuilderView?.getListViews()}
        currentModal={itemBuilderView?.getLastCurrent()}
        openList={this.openComponent}
        onSortChange={this.onSortChange}
        permissions={permissions}
      />
    );
  }

  private getCreateItems() {
    return (
      <CustomDismissible id={`item-tools-${this.id}`} onDismiss={() => this.createOverlayRef?.hide()}>
        <ul id='list-create-container' data-testid='list-create-container' className={'h-[100%] w-[130px] text-center flex flex-col gap-y-2 px-2 py-2'}>
          {RoleHelper.canEdit('TableList') && (
            <li
              className={'cursor-pointer hover:bg-primary hover:text-white text-xs rounded-md'}
              data-testid='List_create_internal'
              onClick={() => this.navigateCreatorPage('file')}
            >
              Internal List
            </li>
          )}
          {RoleHelper.canEdit('QueryList') && (
            <li
              className={'cursor-pointer hover:bg-primary hover:text-white text-xs rounded-md'}
              data-testid='List_create_external'
              onClick={() => this.navigateCreatorPage('query')}
            >
              External List
            </li>
          )}
        </ul>
      </CustomDismissible>
    );
  }

  private getListsFormModals(): string[] {
    const {
      component: { selectedComponentIds },
    } = this.props;
    return selectedComponentIds.map((list: List) => list.name);
  }

  protected async deployAll(): Promise<BulkActionDependencyError[]> {
    return await this.listService.bulkAction(this.getListsFormModals(), 'Deploy');
  }

  protected async undeployAll(): Promise<BulkActionDependencyError[]> {
    return await this.listService.bulkAction(this.getListsFormModals(), 'Undeploy');
  }

  protected async removeAll(): Promise<BulkActionDependencyError[]> {
    return await this.listService.bulkAction(this.getListsFormModals(), 'Remove');
  }

  protected async prepareCount() {
    const { currentCategory } = this.props;
    if (currentCategory == undefined) return;
    const count = await this.listService.getCount(this.getCountFilter().build());
    this.setState({ totalRecord: count });
  }

  protected openCreatePage = (e: any) => {
    this.createOverlayRef?.toggle(e);
  };

  protected openComponent = (name: string) => {
    const { items } = this.state;
    const {
      router: { navigate },
    } = this.props;
    const list = items.find((i) => i.name == name);
    if (list == undefined) return;
    if (list.type == 'INTERNAL_LIST') {
      navigate(`file/edit/${list.name}`);
    } else {
      navigate(`query/edit/${list.name}`);
    }
  };

  protected navigateCreatorPage = (type: string) => {
    const {
      router: { navigate },
    } = this.props;
    navigate(`create/${type}`);
  };
}

interface Props extends ReduxProps, FormicaProps {}

class State {}

const mapStateToProps = (state: RootState) => ({
  itemView: state.page.itemView,
  currentCategory: state.page.currentCategory,
  pagePath: state.page.pagePath,
  component: state.component as ComponentState,
  user: state.auth.user,
  attributes: state.auth.attributes,
});

const mapDispatchToProps = { clearPage, setSelectedItems, changePageFilter };
const connector = connect(mapStateToProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;

export default withFormica(connector(ListContainer));
